import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { authAPI } from '../services/api';
import { isTokenExpired } from '../utils/jwt';

const AuthContext = createContext();

// Cada cuánto se revisa si el token ya expiró, mientras hay sesión activa.
const CHECK_INTERVAL_MS = 30 * 1000;

// Evento disparado por el interceptor de axios cuando el backend responde 401
// (token vencido o inválido), para que el cierre de sesión se refleje aquí.
export const SESSION_EXPIRED_EVENT = 'auth:session-expired';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const checkIntervalRef = useRef(null);

  const clearSession = useCallback(() => {
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  }, []);

  // Cierra la sesión automáticamente cuando el token expira (sin llamar al
  // backend, ya que un token vencido no autenticaría esa petición) y deja una
  // nota para que el login avise al usuario por qué volvió a esa pantalla.
  const forceLogoutByExpiry = useCallback(() => {
    clearSession();
    sessionStorage.setItem('sessionExpired', '1');
  }, [clearSession]);

  const startExpiryWatch = useCallback(() => {
    if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
    checkIntervalRef.current = setInterval(() => {
      const token = localStorage.getItem('token');
      if (!token || isTokenExpired(token)) {
        forceLogoutByExpiry();
      }
    }, CHECK_INTERVAL_MS);
  }, [forceLogoutByExpiry]);

  // Verificar si hay una sesión guardada al cargar la app
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');

    if (storedUser && storedToken) {
      if (isTokenExpired(storedToken)) {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        sessionStorage.setItem('sessionExpired', '1');
      } else {
        try {
          const userData = JSON.parse(storedUser);
          setUser(userData);
          setIsAuthenticated(true);
          startExpiryWatch();
        } catch (error) {
          console.error('Error al parsear usuario guardado:', error);
          localStorage.removeItem('user');
          localStorage.removeItem('token');
        }
      }
    }
    setLoading(false);
  }, [startExpiryWatch]);

  // El interceptor de axios avisa aquí cuando una petición responde 401.
  useEffect(() => {
    const handleSessionExpired = () => forceLogoutByExpiry();
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, [forceLogoutByExpiry]);

  useEffect(() => {
    return () => {
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
    };
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authAPI.login({ email, password });

      if (response.success) {
        const userData = {
          id: response.user.id,
          nombre: response.user.nombre,
          email: response.user.email,
          rol: response.user.rol
        };

        setUser(userData);
        setIsAuthenticated(true);

        // Guardar en localStorage
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('token', response.token);
        sessionStorage.removeItem('sessionExpired');
        startExpiryWatch();

        return { success: true };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      console.error('Error en login:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Error al iniciar sesión. Por favor, intenta nuevamente.'
      };
    }
  };

  const logout = () => {
    clearSession();

    // Llamar al endpoint de logout (opcional)
    try {
      authAPI.logout();
    } catch (error) {
      console.error('Error en logout:', error);
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
